import { useState, useCallback, useMemo } from 'react';
import {
  Flex,
  Text,
  Button,
  Table,
  IconButton,
  Switch,
  Badge,
  Card,
} from '@radix-ui/themes';
import { PlusIcon, Pencil1Icon, TrashIcon } from '@radix-ui/react-icons';
import {
  useGetFieldSyncConfigurations,
  useDeleteFieldSyncConfiguration,
  useUpdateFieldSyncConfiguration,
  QUACK_FIELD_LABELS,
  MAPPING_TYPE_LABELS,
  type FieldSyncConfiguration,
} from '@/v2/entities/field-sync';
import { FieldSyncDialog } from './FieldSyncDialog';
import { Dialog } from '@/components/shared/dialog';
import { toast } from 'sonner';
import styles from './FieldSyncSettings.module.scss';

export function FieldSyncSettings() {
  const { data: configurations = [], isLoading, refetch: refetchConfigurations } = useGetFieldSyncConfigurations();
  const deleteConfiguration = useDeleteFieldSyncConfiguration();
  const updateConfiguration = useUpdateFieldSyncConfiguration();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<FieldSyncConfiguration | null>(
    null
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<FieldSyncConfiguration | null>(
    null
  );

  const handleAdd = useCallback(() => {
    setEditingConfig(null);
    setDialogOpen(true);
  }, []);

  const handleEdit = useCallback((config: FieldSyncConfiguration) => {
    setEditingConfig(config);
    setDialogOpen(true);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!configToDelete) return;

    try {
      await deleteConfiguration.mutateAsync(configToDelete.id);
      toast.success('Field mapping deleted successfully');
      await refetchConfigurations();
      setDeleteDialogOpen(false);
      setConfigToDelete(null);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete field mapping');
    }
  }, [configToDelete, deleteConfiguration, refetchConfigurations]);

  const handleToggle = useCallback(
    async (config: FieldSyncConfiguration) => {
      try {
        await updateConfiguration.mutateAsync({
          id: config.id,
          data: { isEnabled: !config.isEnabled },
        });
        toast.success(
          `Field mapping ${config.isEnabled ? 'disabled' : 'enabled'} successfully`
        );
        await refetchConfigurations();
      } catch (error: any) {
        toast.error(error?.message || 'Failed to update field mapping');
      }
    },
    [updateConfiguration, refetchConfigurations]
  );

  const isEmpty = configurations.length === 0 && !isLoading;

  return (
    <Flex direction="column" gap="4" className={styles.container}>
      {/* Header */}
      <Flex justify="between" align="start">
        <Flex direction="column" gap="1">
          <Flex align="center" gap="2">
            <Text size="6" weight="bold">
              2-Side Sync
            </Text>
            <Badge color="blue" variant="soft" highContrast>
              Beta
            </Badge>
          </Flex>
          <Text size="2" color="gray" highContrast>
            Configure which Quack fields sync to your ticketing system
          </Text>
        </Flex>
        <Button onClick={handleAdd} disabled={isLoading} highContrast>
          <PlusIcon />
          Add Field Mapping
        </Button>
      </Flex>

      {/* Empty State */}
      {isEmpty && (
        <Card className={styles.emptyState}>
          <Flex direction="column" gap="3" align="center" py="6">
            <Text size="3" color="gray" align="center" highContrast>
              No field mappings configured yet.
            </Text>
            <Text size="2" color="gray" align="center" highContrast>
              Add a mapping to start syncing Quack data to your ticketing system.
            </Text>
            <Button onClick={handleAdd} variant="soft" highContrast>
              <PlusIcon />
              Add Your First Field Mapping
            </Button>
          </Flex>
        </Card>
      )}

      {/* Table */}
      {configurations.length > 0 && (
        <Card>
          <Table.Root variant="surface">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Quack Field</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Mapping Type</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Target Field</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Enrichment</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {configurations.map((config) => (
                <Table.Row key={config.id}>
                  <Table.Cell>
                    <Text size="2" weight="medium">
                      {QUACK_FIELD_LABELS[config.quackField]}
                    </Text>
                  </Table.Cell>

                  <Table.Cell>
                    <Badge variant="soft" color="gray" size="1" highContrast>
                      {MAPPING_TYPE_LABELS[config.mappingType]}
                    </Badge>
                  </Table.Cell>

                  <Table.Cell>
                    <Text size="2">{config.targetFieldName}</Text>
                  </Table.Cell>

                  <Table.Cell>
                    <Text size="2" color="gray" highContrast>
                      {config.enrichment?.name || '-'}
                    </Text>
                  </Table.Cell>

                  <Table.Cell>
                    <Switch
                      checked={config.isEnabled}
                      onCheckedChange={() => handleToggle(config)}
                      disabled={updateConfiguration.isLoading}
                      highContrast
                    />
                  </Table.Cell>

                  <Table.Cell>
                    <Flex gap="2">
                      <IconButton
                        size="1"
                        variant="ghost"
                        color="gray"
                        onClick={() => handleEdit(config)}
                        highContrast
                      >
                        <Pencil1Icon />
                      </IconButton>
                      <IconButton
                        size="1"
                        variant="ghost"
                        color="red"
                        onClick={() => {
                          setConfigToDelete(config);
                          setDeleteDialogOpen(true);
                        }}
                        highContrast
                      >
                        <TrashIcon />
                      </IconButton>
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <FieldSyncDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editingConfig={editingConfig}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <Dialog.Content size="small">
          <Dialog.Title>Delete Field Mapping</Dialog.Title>

          <Flex direction="column" gap="4" p="4">
            <Text size="2" color="gray" highContrast>
              Are you sure you want to delete this field mapping? This action cannot be
              undone.
            </Text>

            <Flex gap="3" justify="end" pt="2">
              <Button variant="soft" color="gray" onClick={() => setDeleteDialogOpen(false)} highContrast>
                Cancel
              </Button>
              <Button
                variant="solid"
                color="red"
                onClick={handleDelete}
                disabled={deleteConfiguration.isLoading}
                highContrast
              >
                Delete
              </Button>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog>
    </Flex>
  );
}
